/* De status van een Offerte, en wat eruit volgt (ADR 0018).
 *
 * Opgeslagen: draft (concept), sent (verstuurd), accepted (geaccepteerd), declined
 * (afgewezen); migratie 097 laat niets anders toe. "Verlopen" wordt nooit opgeslagen:
 * een open offerte (concept of verstuurd) met valid_until vóór vandaag is verlopen.
 *
 * "Vandaag" is de lokale dag van de browser, niet UTC: om 23:30 is het nog vandaag.
 * valid_until, sent_on en decided_on zijn kalenderdagen ('YYYY-MM-DD'); daar rekenen
 * we mee als tekst, zonder tijdzone. Puur, zodat het te toetsen is. */

export type OfferteStatus = 'draft' | 'sent' | 'accepted' | 'declined';
export type Weergave = 'concept' | 'verstuurd' | 'verlopen' | 'geaccepteerd' | 'afgewezen';

export const OPEN_STATUSSEN: OfferteStatus[] = ['draft', 'sent'];
/** Zoveel dagen vooruit heet een offerte "verloopt binnenkort" (deze week). */
export const VERLOOPT_BINNEN_DAGEN = 7;
/** Zoveel dagen na het versturen staat de herinnertaak. */
export const HERINNER_NA_DAGEN = 7;

export const WEERGAVE_LABEL: Record<Weergave, string> = {
  concept: 'Concept',
  verstuurd: 'Verstuurd',
  verlopen: 'Verlopen',
  geaccepteerd: 'Geaccepteerd',
  afgewezen: 'Afgewezen',
};

export const WEERGAVE_KLEUR: Record<Weergave, string> = {
  concept: 'bg-slate-100 text-slate-700 border-slate-200',
  verstuurd: 'bg-blue-50 text-blue-800 border-blue-200',
  verlopen: 'bg-red-50 text-red-800 border-red-200',
  geaccepteerd: 'bg-green-50 text-green-800 border-green-200',
  afgewezen: 'bg-zinc-100 text-zinc-600 border-zinc-200',
};

export interface StatusVelden {
  status: string;
  valid_until: string | null;
  sent_on?: string | null;
  decided_on?: string | null;
  quote_date?: string | null;
  created_at?: string | null;
}

const DAG_RE = /^\d{4}-\d{2}-\d{2}$/;

/** YYYY-MM-DD in de tijdzone van de browser. */
export function lokaleDag(d: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** Kalenderdag plus n dagen, zonder tijdzone (zomertijd speelt niet mee). */
export function plusDagen(dag: string, n: number): string {
  if (!DAG_RE.test(dag)) throw new Error(`'${dag}' is geen dag (YYYY-MM-DD).`);
  const t = Date.UTC(Number(dag.slice(0, 4)), Number(dag.slice(5, 7)) - 1, Number(dag.slice(8, 10))) + n * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/** Aantal dagen van `van` tot `tot` (positief als `tot` later is). */
export function dagenTussen(van: string, tot: string): number {
  const d = (s: string) => Date.UTC(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));
  return Math.round((d(tot) - d(van)) / 86_400_000);
}

/** De kalenderdag van een datum of tijdstip: een date blijft wat hij is, een tijdstip wordt de lokale dag. */
export function dagVan(waarde: string | null | undefined): string | null {
  if (!waarde) return null;
  if (DAG_RE.test(waarde)) return waarde;
  const d = new Date(waarde);
  return Number.isNaN(d.getTime()) ? null : lokaleDag(d);
}

export const isOpen = (q: Pick<StatusVelden, 'status'>) => q.status === 'draft' || q.status === 'sent';

export function isVerlopen(q: StatusVelden, vandaag: string): boolean {
  return isOpen(q) && Boolean(q.valid_until) && q.valid_until! < vandaag;
}

export function weergave(q: StatusVelden, vandaag: string): Weergave {
  if (isVerlopen(q, vandaag)) return 'verlopen';
  switch (q.status) {
    case 'sent': return 'verstuurd';
    case 'accepted': return 'geaccepteerd';
    case 'declined': return 'afgewezen';
    default: return 'concept';
  }
}

export interface Signaal {
  soort: 'verloopt' | 'verlopen';
  /** Dagen tot het verloopt (0 = vandaag de laatste dag), of dagen sinds het verlopen is. */
  dagen: number;
  tekst: string;
}

/** 'verloopt morgen' of 'verlopen'; null als er niets te melden is (dicht, geen datum, of nog ver weg). */
export function signaal(q: StatusVelden, vandaag: string): Signaal | null {
  if (!isOpen(q) || !q.valid_until) return null;
  const dagen = dagenTussen(vandaag, q.valid_until);
  if (dagen < 0) return { soort: 'verlopen', dagen: -dagen, tekst: 'verlopen' };
  if (dagen > VERLOOPT_BINNEN_DAGEN) return null;
  const tekst = dagen === 0 ? 'verloopt vandaag' : dagen === 1 ? 'verloopt morgen' : `verloopt over ${dagen} dagen`;
  return { soort: 'verloopt', dagen, tekst };
}

/**
 * Sinds wanneer de offerte open staat: verstuurd telt vanaf de verzenddag (zo lang
 * wacht je op de klant), een concept vanaf de offertedatum of het aanmaken. Een
 * besloten offerte staat niet open: null.
 */
export function openSinds(q: StatusVelden): string | null {
  if (q.status === 'sent') return q.sent_on ?? null;
  if (q.status === 'draft') return q.quote_date ?? dagVan(q.created_at) ?? null;
  return null;
}

/** '3 dagen', '2 wkn', '4 mnd' tussen twee dagen; 'vandaag' bij 0. */
export function duur(van: string | null, vandaag: string): string {
  if (!van) return '—';
  const d = dagenTussen(van, vandaag);
  if (d <= 0) return 'vandaag';
  if (d === 1) return '1 dag';
  if (d < 14) return `${d} dagen`;
  if (d < 60) return `${Math.floor(d / 7)} wkn`;
  if (d < 365) return `${Math.floor(d / 30)} mnd`;
  const jaar = Math.floor(d / 365);
  return `${jaar} jaar`;
}
